import { useState, useEffect, useRef } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, fmtEuro, fmtDeadline, deadlineDaysLeft, capitalizeName } from './lib/format.js';
import { uploadCustomerLogo, deleteOldCustomerLogos } from './lib/logo.js';
import { Modal, ProjectAvatar, BudgetLight, Avatar } from './components.jsx';
import { compareProjectsByStatus, projectReferenceDate, StatusChip, GoalSection } from './projects.jsx';
import { IconPlus, IconX, IconChevronRight } from './icons.jsx';

const CUSTOMER_COLORS = ['#6366F1','#10B981','#F59E0B','#EF4444','#0EA5E9','#B45309','#EC4899','#8B5CF6'];

// --- Reusable customer avatar (logo if present, colored letter otherwise) ---
function CustomerAvatar({ customer, size = 44, override }) {
  // override = { src, color, initial } — used by the modal for live preview.
  const src = override?.src ?? customer?.logo_url;
  const color = override?.color ?? customer?.color ?? '#6366F1';
  const initial = override?.initial ?? customer?.initial ?? customer?.name?.[0] ?? '?';

  if (src) {
    return (
      <div style={{
        width: size, height: size, borderRadius: Math.round(size * 0.22),
        background: '#fff', display: 'grid', placeItems: 'center',
        flex: `0 0 ${size}px`, overflow: 'hidden',
        border: '1px solid var(--stroke)',
      }}>
        <img src={src} alt=""
             style={{ maxWidth: '85%', maxHeight: '85%', objectFit: 'contain', display: 'block' }}/>
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: Math.round(size * 0.22),
      background: `linear-gradient(135deg, ${color}, ${color}99)`,
      color: '#fff', display: 'grid', placeItems: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.4),
      flex: `0 0 ${size}px`,
    }}>{initial}</div>
  );
}

function CustomerFormModal({ customer, onClose, onSaved }) {
  const { addCustomer, updateCustomer, deleteCustomer } = useData();
  const isEdit = !!customer;
  const fileRef = useRef(null);

  const [name, setName] = useState(customer?.name || '');
  const [city, setCity] = useState(customer?.city || '');
  const [industry, setIndustry] = useState(customer?.industry || '');
  const [website, setWebsite] = useState(customer?.website || '');
  const [color, setColor] = useState(customer?.color || CUSTOMER_COLORS[0]);

  // Logo state. logoUrl = persisted URL (from DB). pendingBlob = local blob we
  // haven't uploaded yet. previewUrl = object URL for the pending blob.
  const [logoUrl, setLogoUrl] = useState(customer?.logo_url || null);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [pendingType, setPendingType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [logoNotice, setLogoNotice] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  // Manage object URL lifecycle to avoid leaks
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const initial = name.trim()[0]?.toUpperCase() || '?';
  const canSave = name.trim().length >= 2;
  const displayedLogoSrc = previewUrl || logoUrl;

  function setBlobAsPending(blob, contentType) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(blob);
    setPendingBlob(blob);
    setPendingType(contentType || blob.type);
    setPreviewUrl(url);
    setLogoNotice(null);
  }

  function handleManualPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoNotice({ kind: 'error', text: 'Bitte eine Bilddatei wählen.' });
      return;
    }
    setBlobAsPending(file, file.type);
    setLogoNotice({ kind: 'ok', text: 'Logo gewählt — speichern, um zu übernehmen.' });
  }

  function handleClearLogo() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingBlob(null);
    setPendingType(null);
    setPreviewUrl(null);
    setLogoUrl(null); // marks for removal on save
    setLogoNotice(null);
  }

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      const basePayload = {
        name: name.trim(),
        city: city.trim() || null,
        industry: industry.trim() || null,
        website: website.trim() || null,
        initial,
        color,
      };

      // Determine the customer id we'll use for storage paths.
      // For new customers, generate UUID client-side so storage path matches.
      const id = customer?.id || (typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : null);

      // 1. Upsert the customer row (without logo_url change yet)
      let saved;
      if (isEdit) {
        saved = await updateCustomer(customer.id, { ...basePayload, logo_url: logoUrl });
      } else {
        saved = await addCustomer(id ? { id, ...basePayload, logo_url: null } : { ...basePayload, logo_url: null });
      }

      const customerId = saved.id;

      // 2. If we have a pending blob, upload + patch logo_url.
      let finalLogoUrl = logoUrl;
      if (pendingBlob) {
        const url = await uploadCustomerLogo(customerId, pendingBlob, pendingType);
        finalLogoUrl = url;
        await updateCustomer(customerId, { logo_url: url });
        // best-effort: clean up older versions
        deleteOldCustomerLogos(customerId, url);
      } else if (isEdit && customer.logo_url && !logoUrl) {
        // Logo was cleared: remove old files
        deleteOldCustomerLogos(customerId, null);
      }

      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    const ok = window.confirm(
      `Kunde „${customer.name}" wirklich löschen? Projekte bleiben erhalten, verlieren aber die Kunden-Zuordnung.`
    );
    if (!ok) return;
    setDeleting(true); setErr(null);
    try {
      await deleteCustomer(customer.id);
      // best-effort logo cleanup
      deleteOldCustomerLogos(customer.id, null);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Löschen');
      setDeleting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="eyebrow">{isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}</div>
          <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
            {name || (isEdit ? '—' : 'Wie heißt der Kunde?')}
          </h2>
        </div>
        <button className="icon-btn" onClick={onClose}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, overflowY: 'auto' }}>
        <div className="grid grid-2" style={{ gap: 16 }}>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Name *</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} autoFocus
                   placeholder="z.B. Möbelwerk Müller"
                   onKeyDown={e => e.key === 'Enter' && save()}/>
          </div>
          <div className="field">
            <label>Stadt</label>
            <input className="input" value={city} onChange={e => setCity(e.target.value)} placeholder="z.B. Coburg"/>
          </div>
          <div className="field">
            <label>Branche</label>
            <input className="input" value={industry} onChange={e => setIndustry(e.target.value)} placeholder="z.B. Automotive"/>
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Website</label>
            <input className="input" value={website} onChange={e => setWebsite(e.target.value)}
                   placeholder="z.B. markenegger.com" type="url"/>
          </div>
        </div>

        {/* Logo section */}
        <div style={{ marginTop: 24, padding: 16, background: 'var(--bg-sunken)', borderRadius: 12, border: '1px solid var(--stroke)' }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>Logo</div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <CustomerAvatar
              customer={customer || { name }}
              size={64}
              override={{ src: displayedLogoSrc, color, initial }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => fileRef.current?.click()}>
                  {displayedLogoSrc ? 'Anderes Logo wählen' : 'Logo hochladen'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleManualPick}
                       style={{ display: 'none' }}/>
                {displayedLogoSrc && (
                  <button className="btn btn-ghost" onClick={handleClearLogo}
                          style={{ color: 'var(--crit)' }}>
                    Logo entfernen
                  </button>
                )}
              </div>
              {logoNotice && (
                <div className="tiny" style={{
                  color: logoNotice.kind === 'error' ? 'var(--crit)' :
                         logoNotice.kind === 'warn'  ? 'var(--warn)' :
                                                      'var(--ok)',
                }}>
                  {logoNotice.text}
                </div>
              )}
              {!displayedLogoSrc && !logoNotice && (
                <div className="tiny muted">
                  PNG, JPG oder SVG. Wenn kein Logo hochgeladen wird, zeigen wir den Anfangsbuchstaben in der Fallback-Farbe.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fallback color (used when no logo) */}
        <div className="field" style={{ marginTop: 20 }}>
          <label>Fallback-Farbe (wenn kein Logo)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {CUSTOMER_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)} style={{
                width: 28, height: 28, borderRadius: 8,
                border: color === c ? '2px solid var(--ink)' : '2px solid transparent',
                background: c, cursor: 'pointer', padding: 0, outline: 0,
              }}/>
            ))}
          </div>
        </div>

        {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{err}</div>}
      </div>

      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        {isEdit ? (
          <button className="btn" onClick={handleDelete} disabled={saving || deleting}
                  style={{ color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}>
            {deleting ? 'Lösche…' : 'Kunde löschen'}
          </button>
        ) : <div/>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving || deleting}>Abbrechen</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave || saving || deleting}
                  style={{ opacity: (canSave && !saving && !deleting) ? 1 : 0.45 }}>
            {saving ? 'Speichere…' : isEdit ? 'Änderungen speichern' : 'Kunde anlegen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CustomersPage({ onNavigate }) {
  const { customers, projects, projectUsage } = useData();
  const [modal, setModal] = useState(null);

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Kunden</h1>
        <div className="chip">{customers.length} {customers.length === 1 ? 'Kunde' : 'Kunden'}</div>
        <div className="topbar-spacer"/>
        <button className="btn btn-primary" onClick={() => setModal({})}>
          <IconPlus size={14}/> Neuer Kunde
        </button>
      </div>
      <div className="content">
        {customers.length === 0 ? (
          <div className="card card-pad-lg">
            <div className="tiny muted">Noch keine Kunden angelegt. Klick „Neuer Kunde" oben rechts.</div>
          </div>
        ) : (
          <div className="grid grid-3">
            {customers.map(c => {
              const projs = projects.filter(p => p.customer_id === c.id);
              const totals = projs.reduce((acc, p) => {
                const u = projectUsage[p.id] || { minutes: 0, euro: 0 };
                return { min: acc.min + u.minutes, eur: acc.eur + u.euro };
              }, { min: 0, eur: 0 });
              return (
                <div key={c.id} className="card card-pad"
                     style={{ cursor: 'pointer', transition: 'background 150ms, border-color 150ms' }}
                     onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                     onClick={() => onNavigate({ view: 'customer', id: c.id })}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CustomerAvatar customer={c} size={44}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                      <div className="tiny muted">{[c.industry, c.city].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                    <IconChevronRight size={14}/>
                  </div>
                  <hr className="div"/>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div>
                      <div className="eyebrow">Projekte</div>
                      <div className="serif" style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>{projs.length}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Zeit</div>
                      <div className="serif" style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>{fmtMin(totals.min)}</div>
                    </div>
                    <div>
                      <div className="eyebrow">Umsatz</div>
                      <div className="serif" style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>{fmtEuro(totals.eur)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && (
        <CustomerFormModal
          customer={modal.customer}
          onClose={() => setModal(null)}
          onSaved={() => {}}
        />
      )}
    </>
  );
}

function CustomerDetail({ id, onBack, onNavigate, onOpenNewProject }) {
  const { customers, projects, projectUsage, isAdmin, profileById } = useData();
  const [showEdit, setShowEdit] = useState(false);
  const c = customers.find(x => x.id === id);
  if (!c) {
    return (
      <div className="content">
        <div className="card card-pad-lg">
          <div className="tiny muted">Kunde nicht gefunden.</div>
          <button className="btn" style={{ marginTop: 12 }} onClick={onBack}>Zurück</button>
        </div>
      </div>
    );
  }
  const projs = projects
    .filter(p => p.customer_id === c.id)
    .slice()
    .sort(compareProjectsByStatus);
  const totals = projs.reduce((acc, p) => {
    const u = projectUsage[p.id] || { minutes: 0, euro: 0 };
    return { min: acc.min + u.minutes, eur: acc.eur + u.euro };
  }, { min: 0, eur: 0 });

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <button className="btn btn-ghost" onClick={onBack} style={{ padding: '4px 8px' }}>← Kunden</button>
        <div style={{ width: 1, height: 20, background: 'var(--stroke)' }}/>
        <CustomerAvatar customer={c} size={40}/>
        <div>
          <h1 style={{ fontSize: 18 }}>{c.name}</h1>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            {[c.industry, c.city].filter(Boolean).join(' · ') || '—'}
            {c.website && <> · <a href={/^https?:\/\//.test(c.website) ? c.website : `https://${c.website}`}
                                  target="_blank" rel="noreferrer"
                                  style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {c.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')}
            </a></>}
          </div>
        </div>
        <div className="topbar-spacer"/>
        {isAdmin && (
          <>
            <button className="btn" onClick={() => setShowEdit(true)}>Bearbeiten</button>
            <button className="btn btn-primary" onClick={() => onOpenNewProject?.(c.id)}>
              <IconPlus size={14}/> Neues Projekt
            </button>
          </>
        )}
      </div>
      <div className="content">
        <div className="grid grid-12" style={{ marginBottom: 20 }}>
          <div className="card col-4 card-pad-lg">
            <div className="eyebrow">Projekte</div>
            <div className="serif" style={{ fontSize: 28, fontWeight: 500, marginTop: 6 }}>{projs.length}</div>
          </div>
          <div className="card col-4 card-pad-lg">
            <div className="eyebrow">Erfasste Zeit</div>
            <div className="serif" style={{ fontSize: 28, fontWeight: 500, marginTop: 6 }}>{fmtMin(totals.min)}</div>
          </div>
          <div className="card col-4 card-pad-lg">
            <div className="eyebrow">Umsatz</div>
            <div className="serif" style={{ fontSize: 28, fontWeight: 500, marginTop: 6 }}>{fmtEuro(totals.eur)}</div>
          </div>
        </div>

        {/* Customer goal (Nordstern) — inherited by all of this customer's projects */}
        <GoalSection customer={c}/>

        <div className="card card-pad-lg">
          <div className="card-title">
            <span>Projekte</span>
            <span className="sub">{projs.length === 0 ? 'keine' : `${projs.length}`}</span>
          </div>
          {projs.length === 0 ? (
            <div className="tiny muted" style={{ padding: '12px 4px' }}>
              Noch keine Projekte für diesen Kunden.
              {isAdmin && onOpenNewProject && (
                <> <span
                  onClick={() => onOpenNewProject(c.id)}
                  style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
                >Direkt anlegen</span>.</>
              )}
            </div>
          ) : projs.map(p => {
            const usage = projectUsage[p.id] || { minutes: 0 };
            const pct = p.budget_minutes ? Math.round((usage.minutes / p.budget_minutes) * 100) : 0;
            const days = deadlineDaysLeft(p.deadline, projectReferenceDate(p));
            const lead = p.lead_id ? profileById(p.lead_id) : null;
            const leadName = lead ? (capitalizeName(lead.full_name) || lead.email) : null;
            return (
              <div key={p.id} className="row"
                   style={{ gridTemplateColumns: '32px 1fr 40px 140px 140px 60px', cursor: 'pointer' }}
                   onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                   onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                   onClick={() => onNavigate({ view: 'project', id: p.id })}>
                <ProjectAvatar project={p} size={28}/>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{p.title}</div>
                  <div style={{ marginTop: 3 }}>
                    <StatusChip status={p.status} wasOverBudget={p.was_over_budget}/>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }} title={leadName ? `Projektlead: ${leadName}` : 'Kein Projektlead'}>
                  {lead
                    ? <Avatar user={lead} size={26} badge={{ emoji: '⭐', title: 'Projektlead' }}/>
                    : <div style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px dashed var(--stroke-2)' }}/>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  {p.deadline ? (
                    <>
                      {fmtDeadline(p.deadline)}
                      <div className={`tiny ${days < 14 ? '' : 'muted'}`} style={{ color: days < 14 ? 'var(--crit)' : undefined }}>in {days} Tagen</div>
                    </>
                  ) : <span className="muted">—</span>}
                </div>
                <div>
                  {p.budget_minutes ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div className="bar" style={{ flex: 1 }}>
                          <span style={{ width: `${Math.min(100, pct)}%`, background: pct < 70 ? 'var(--ok)' : pct < 90 ? 'var(--warn)' : 'var(--crit)' }}/>
                        </div>
                        <span className="mono tiny" style={{ color: 'var(--ink-2)' }}>{pct}%</span>
                      </div>
                      <div className="tiny muted" style={{ marginTop: 2 }}>{fmtMin(usage.minutes)} / {fmtMin(p.budget_minutes)}</div>
                    </>
                  ) : <span className="muted tiny">—</span>}
                </div>
                <div>{p.budget_minutes && <BudgetLight pct={pct}/>}</div>
              </div>
            );
          })}
        </div>
      </div>

      {showEdit && (
        <CustomerFormModal
          customer={c}
          onClose={() => setShowEdit(false)}
          onSaved={() => {}}
        />
      )}
    </>
  );
}

export { CustomersPage, CustomerDetail, CustomerAvatar };
